import type { ReactElement } from 'react';
import { useMemo, useState } from 'react';

import { useListArticlesQuery } from '@/api/articlesApi';
import { Input } from '@/components/Input';

function formatDate(iso: string | null): string {
    return iso === null ? '—' : new Date(iso).toLocaleDateString('ru-RU');
}

export function ArticlesPage(): ReactElement {
    const [searchInput, setSearchInput] = useState('');
    const [submittedSearch, setSubmittedSearch] = useState('');

    const queryArg = useMemo(
        () => ({
            ...(submittedSearch ? { search: submittedSearch } : {}),
            limit: 50,
            offset: 0
        }),
        [submittedSearch]
    );

    const { data, isLoading, isError } = useListArticlesQuery(queryArg);

    const items = data?.items ?? [];

    return (
        <div data-testid="articles-page" className="space-y-4">
            <div className="flex items-center justify-between">
                <h1 className="text-2xl font-semibold text-gray-900">Статьи</h1>
                {data && (
                    <span className="text-sm text-gray-500" data-testid="articles-total">
                        Всего: {data.total}
                    </span>
                )}
            </div>

            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    setSubmittedSearch(searchInput.trim());
                }}
                className="flex items-end gap-3 rounded border border-gray-200 bg-white p-3"
                data-testid="articles-filters"
            >
                <div className="flex-1">
                    <Input
                        label="Поиск"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        placeholder="Заголовок статьи"
                        data-testid="articles-search-input"
                    />
                </div>
                <button
                    type="submit"
                    data-testid="articles-search-submit"
                    className="rounded bg-gray-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-gray-800"
                >
                    Найти
                </button>
            </form>

            {isLoading && <div className="text-sm text-gray-500">Загрузка...</div>}
            {isError && (
                <div className="text-sm text-red-600" data-testid="articles-error">
                    Не удалось загрузить статьи
                </div>
            )}

            {!isLoading && !isError && (
                <div
                    className="overflow-hidden rounded border border-gray-200 bg-white"
                    data-testid="articles-table"
                >
                    <div className="grid grid-cols-[80px_minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)_120px_80px_80px] gap-2 border-b border-gray-200 bg-gray-50 px-3 py-2 text-xs font-medium uppercase text-gray-600">
                        <span>ID</span>
                        <span>Заголовок</span>
                        <span>Slug</span>
                        <span>Автор</span>
                        <span>Опубликована</span>
                        <span className="text-right">Просмотры</span>
                        <span className="text-right">Лайки</span>
                    </div>

                    {items.length === 0 && (
                        <div className="px-3 py-6 text-center text-sm text-gray-500">
                            Статьи не найдены
                        </div>
                    )}

                    {items.map((article) => (
                        <div
                            key={article.id}
                            className="grid grid-cols-[80px_minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)_120px_80px_80px] items-center gap-2 border-b border-gray-100 px-3 py-2 text-sm last:border-b-0"
                            data-testid={`article-row-${article.id}`}
                        >
                            <span className="truncate font-mono text-xs text-gray-500">
                                {article.id.slice(0, 8)}
                            </span>
                            <span className="truncate text-gray-900">{article.title}</span>
                            <span className="truncate font-mono text-xs text-gray-600">
                                {article.slug}
                            </span>
                            <span className="truncate text-gray-700">
                                {article.authorDisplayName}
                            </span>
                            <span className="text-xs text-gray-600">
                                {formatDate(article.publishedAt)}
                            </span>
                            <span className="text-right text-gray-700">{article.viewsCount}</span>
                            <span className="text-right text-gray-700">{article.likesCount}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
